import React, { ReactNode, useCallback, useMemo, useRef } from "react";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";

import { useChat } from "@ai-sdk/react";
import { AiChatType } from "@src/types.generated";
import { Message } from "ai";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "@src/features/assistantChat/aiChat.generated";
import { getCustomDynamicHeaders } from "@src/lib/graphql/apollo";

interface ChatRuntimeProviderProps {
  children: ReactNode;
  chatType: AiChatType;
}

export interface CustomChatAttachment {
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  canvasContent?: string;
}

type CustomMessageData = {
  brandDetailsId?: string;
  attachments?: CustomChatAttachment[];
  suggestedQuestionId?: string;
};

type CustomMessageInput = {
  role: "user";
  content: string;
  customMessageData?: CustomMessageData;
};

export type CustomMessage = Message & {
  customMessageData?: CustomMessageData;
};

type ChatContextType = Omit<
  ReturnType<typeof useChat>,
  "append" | "handleSubmit" | "input" | "handleInputChange" | "messages"
> & {
  submitMessage: (input: CustomMessageInput) => void;
  messages: CustomMessage[];
  aiSessionId: string;
  chatType: AiChatType;
  stop: () => void;
  error: Error | undefined;
  clearError: () => void;
};

// Context to provide chat functionality to child components
export const ChatContext = React.createContext<ChatContextType | null>(null);

export const ChatRuntimeProvider: React.FC<ChatRuntimeProviderProps> = ({
  children,
  chatType,
}) => {
  const { conversationId, selectedSession, setStreamingError } =
    useChatSidebar(chatType);
  const abortControllerRef = useRef<AbortController | null>(null);
  const chat = useChat({
    id: conversationId,
    api: `${process.env.NEXT_PUBLIC_API_URL}/v1/knowledge-base-chat/stream/`,
    credentials: "include",
    body: {
      id: conversationId,
      conversation_type: chatType,
    },
    initialMessages: selectedSession?.messages || [],
    // Handle streaming data that includes annotations
    streamProtocol: "data",
    sendExtraMessageFields: true,
    experimental_throttle: 150,
    onError: (error) => {
      // Store error in Zustand store when it occurs
      setStreamingError(error);
    },
    fetch: async (input, init) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      return fetch(input, {
        ...init,
        signal: controller.signal,
        headers: {
          ...init?.headers,
          ...getCustomDynamicHeaders(),
          "Content-Type": "application/json",
        },
      });
    },
  });

  const submitMessage = useCallback(
    (input: CustomMessageInput) => {
      // Clear error when user submits a new message (not a retry)
      setStreamingError(null);

      chat.append({
        role: input.role,
        content: input.content,
        // @ts-ignore
        customMessageData: input.customMessageData,
      });
    },
    [chat, setStreamingError],
  );

  const clearError = useCallback(() => {
    setStreamingError(null);
  }, [setStreamingError]);

  const chatContextValue: ChatContextType = useMemo(
    () => ({
      ...chat,
      submitMessage,
      aiSessionId: conversationId,
      chatType,
      error: selectedSession?.streamingError || undefined,
      clearError,
      stop: () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      },
    }),
    [
      chat,
      submitMessage,
      conversationId,
      chatType,
      selectedSession?.streamingError,
      clearError,
    ],
  );

  return (
    <ChatContext.Provider value={chatContextValue}>
      {children}
    </ChatContext.Provider>
  );
};

// Hook to use chat context
export const useChatContext = (): ChatContextType => {
  const context = React.useContext(ChatContext);
  if (!context) {
    throw new Error(
      "useChatContext must be used within ChatRuntimeProviderNew",
    );
  }
  return context;
};
